import React, { useState } from 'react';
import { US_STATES } from '../../lib/usStates';
import { useNavigate } from 'react-router-dom';
import {
  Eye, EyeOff, Lock, Mail, Phone, User, Building2, FileText,
  ShieldCheck, Check, X, Plus, ArrowLeft, Stethoscope, Network, PawPrint, Pill
} from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useAuthStore } from '../../store/useAuthStore';
import { PARTNER_ONBOARDING_RETURN } from '../../lib/partnerOnboarding';

interface InvitationData {
  email: string;
  firstName?: string;
  lastName?: string;
  telemedOnly?: boolean;
  npiNumber?: string;
  phone?: string;
  clinicName?: string;
}

interface License {
  state: string;
  licenseNumber?: string;
  source?: 'nppes' | 'self';
}

interface NetworkOption {
  _id: string;
  name: string;
  perVisitFeeCents: number;
}

type Step =
  | 'personal'
  | 'sms'
  | 'npi-question'
  | 'npi-entry'
  | 'licenses'
  | 'network-opt'
  | 'no-npi-network'
  | 'vet-licenses'
  | 'pharmacy-npi'
  | 'pharmacy-licenses';

const inputCls = 'w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const btnPrimary = 'w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium';

// Hoisted to module scope so it keeps a stable component identity across renders —
// defining it inside the wizard would remount inputs on every keystroke (focus loss).
const WizardShell: React.FC<{
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  error?: string;
  progressIndex: number;
}> = ({ children, title, subtitle, onBack, error, progressIndex }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
    <div className="max-w-lg w-full">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center gap-2 mb-6">
          {onBack && (
            <button type="button" onClick={onBack} className="text-gray-400 hover:text-gray-600" aria-label="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex-1">
            <div className="flex gap-1.5 mb-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= progressIndex ? 'bg-blue-600' : 'bg-gray-200'}`} />
              ))}
            </div>
          </div>
        </div>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="text-gray-600 mt-2">{subtitle}</p>}
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {children}
      </div>
    </div>
  </div>
);

// Client-side NPI check (10 digits + CMS Luhn). Mirrors the backend so we can
// give instant feedback before the live NPPES lookup.
function isValidNpi(npi: string): boolean {
  if (!/^\d{10}$/.test(npi)) return false;
  if (!['1', '2'].includes(npi[0])) return false;
  const base = '80840' + npi.slice(0, 9);
  const digits = base.split('').map(Number);
  let sum = 0;
  for (let i = digits.length - 1, dbl = true; i >= 0; i--, dbl = !dbl) {
    let d = digits[i];
    if (dbl) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return (sum + Number(npi[9])) % 10 === 0;
}

export const PhysicianOnboardingWizard: React.FC<{
  invitationToken: string;
  invitation: InvitationData;
  partnerOnboarding?: { id: string; name: string; prescribingMode?: 'own_physician' | 'doctor_network' };
}> = ({ invitationToken, invitation, partnerOnboarding }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('personal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Personal info
  const [firstName, setFirstName] = useState(invitation.firstName || '');
  const [lastName, setLastName] = useState(invitation.lastName || '');
  const [phone, setPhone] = useState(invitation.phone || '');
  const [clinicName, setClinicName] = useState(invitation.clinicName || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // SMS
  const [smsCode, setSmsCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // NPI + licenses
  const [npiNumber, setNpiNumber] = useState(invitation.npiNumber || '');
  const [nppesName, setNppesName] = useState<string | null>(null);
  const [licenses, setLicenses] = useState<License[]>([]);

  // Network
  const [useNetwork, setUseNetwork] = useState(false);
  const [networks, setNetworks] = useState<NetworkOption[]>([]);
  const [selectedNetworkId, setSelectedNetworkId] = useState('');

  const post = async (url: string, body: any) => {
    const res: any = await apiClient.post(url, body);
    return res;
  };

  // ── Step 1: personal info → send OTP ──────────────────────────────
  const handlePersonalNext = async () => {
    setError('');
    if (!firstName.trim() || !lastName.trim()) return setError('Please enter your first and last name.');
    if (!phone.replace(/\D/g, '') || phone.replace(/\D/g, '').length < 10) return setError('Please enter a valid phone number.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      const res = await post('/auth/onboarding/send-otp', { invitationToken, phone });
      if (!res.success) return setError(res.error || 'Could not send the verification code.');
      setStep('sms');
      setResendCooldown(30);
      const t = setInterval(() => setResendCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not send the verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      const res = await post('/auth/onboarding/send-otp', { invitationToken, phone });
      if (!res.success) return setError(res.error || 'Could not resend the code.');
      setResendCooldown(30);
      const t = setInterval(() => setResendCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not resend the code.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: verify OTP ────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    setError('');
    if (!/^\d{6}$/.test(smsCode)) return setError('Enter the 6-digit code we texted you.');
    setLoading(true);
    try {
      const res = await post('/auth/onboarding/verify-otp', { invitationToken, phone, code: smsCode });
      if (!res.success) return setError(res.error || 'That code is invalid or expired.');
      if (partnerOnboarding?.prescribingMode === 'doctor_network') { await submit({ hasNpi: false }); return; }
      setStep('npi-question');
    } catch (e: any) {
      setError(e.response?.data?.error || 'That code is invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: has NPI? ──────────────────────────────────────────────
  const chooseHasNpi = (hasNpi: boolean) => {
    setError('');
    if (hasNpi) {
      setStep('npi-entry');
    } else {
      loadNetworks();
      setStep('no-npi-network');
    }
  };

  // Veterinarian path: no NPI, no network — prescribes under their own state
  // veterinary license(s), entered manually (no NPPES prefill for vets).
  const chooseVet = () => {
    setError('');
    setLicenses((ls) => (ls.length > 0 ? ls : [{ state: '', licenseNumber: '', source: 'self' }]));
    setStep('vet-licenses');
  };

  // Pharmacy path: transfers scripts written by outside doctors and fills them
  // under its OWN pharmacy NPI, so the NPI is required (unlike the vet path).
  // No telemed network — the pharmacy never prescribes.
  const choosePharmacy = () => {
    setError('');
    setStep('pharmacy-npi');
  };

  const handlePharmacyNpiLookup = async () => {
    setError('');
    if (!isValidNpi(npiNumber)) return setError('Please enter a valid 10-digit NPI number.');
    setLoading(true);
    try {
      const res = await post('/auth/onboarding/npi-lookup', { invitationToken, npiNumber });
      if (!res.success) return setError(res.error || 'We could not look up that NPI.');
      const data = res.data;
      if (data.found) {
        // A pharmacy is usually an organization (type-2) NPI, so the registry
        // returns an organization name rather than a person's name.
        const orgName = data.organizationName || [data.firstName, data.lastName].filter(Boolean).join(' ');
        setNppesName(orgName || null);
        if (orgName && !clinicName.trim()) setClinicName(orgName);
        setLicenses((data.licenses || []).map((l: any) => ({ state: l.state, licenseNumber: l.licenseNumber, source: 'nppes' as const })));
      } else {
        setNppesName(null);
        setLicenses([]);
      }
      setLicenses((ls) => (ls.length > 0 ? ls : [{ state: '', licenseNumber: '', source: 'self' }]));
      setStep('pharmacy-licenses');
    } catch (e: any) {
      setError(e.response?.data?.error || 'We could not look up that NPI.');
    } finally {
      setLoading(false);
    }
  };

  const handlePharmacyLicensesNext = () => {
    setError('');
    const cleaned = licenses.filter((l) => l.state.trim());
    if (cleaned.length === 0) return setError('Add the state your pharmacy is permitted in.');
    if (cleaned.some((l) => !l.licenseNumber?.trim()))
      return setError('A pharmacy permit/license number is required for each state.');
    const states = cleaned.map((l) => l.state.toUpperCase());
    if (new Set(states).size !== states.length) return setError('You have duplicate states in your permit list.');
    setLicenses(cleaned);
    submit({ isPharmacy: true });
  };

  const handleVetLicensesNext = () => {
    setError('');
    const cleaned = licenses.filter((l) => l.state.trim());
    if (cleaned.length === 0) return setError('Add at least one state veterinary license.');
    if (cleaned.some((l) => !l.licenseNumber?.trim()))
      return setError('A license number is required for each state (it replaces the NPI on your scripts).');
    const states = cleaned.map((l) => l.state.toUpperCase());
    if (new Set(states).size !== states.length) return setError('You have duplicate states in your license list.');
    setLicenses(cleaned);
    submit({ isVet: true });
  };

  const loadNetworks = async () => {
    try {
      const res = await post('/auth/onboarding/networks', { invitationToken });
      if (res.success && Array.isArray(res.data)) {
        setNetworks(res.data);
        if (res.data.length === 1) setSelectedNetworkId(res.data[0]._id);
      }
    } catch { /* non-fatal; the step shows an empty-state */ }
  };

  // ── Step 4: NPI entry + live NPPES lookup ─────────────────────────
  const handleNpiLookup = async () => {
    setError('');
    if (!isValidNpi(npiNumber)) return setError('Please enter a valid 10-digit NPI number.');
    setLoading(true);
    try {
      const res = await post('/auth/onboarding/npi-lookup', { invitationToken, npiNumber });
      if (!res.success) return setError(res.error || 'We could not look up that NPI.');
      const data = res.data;
      if (!data.found) {
        // Registry miss — let them proceed and enter licenses manually.
        setNppesName(null);
        setLicenses([]);
      } else {
        setNppesName([data.firstName, data.lastName].filter(Boolean).join(' ') || null);
        setLicenses((data.licenses || []).map((l: any) => ({ state: l.state, licenseNumber: l.licenseNumber, source: 'nppes' as const })));
      }
      setStep('licenses');
    } catch (e: any) {
      setError(e.response?.data?.error || 'We could not look up that NPI.');
    } finally {
      setLoading(false);
    }
  };

  // License editing helpers
  const addLicense = () => setLicenses((ls) => [...ls, { state: '', licenseNumber: '', source: 'self' }]);
  const removeLicense = (i: number) => setLicenses((ls) => ls.filter((_, idx) => idx !== i));
  const updateLicense = (i: number, field: keyof License, value: string) =>
    setLicenses((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

  const handleLicensesNext = () => {
    setError('');
    const cleaned = licenses.filter((l) => l.state.trim());
    if (partnerOnboarding && (!cleaned.length || cleaned.some(l => !l.licenseNumber?.trim()))) {
      return setError('Add at least one state license and its license number.');
    }
    const states = cleaned.map((l) => l.state.toUpperCase());
    if (new Set(states).size !== states.length) return setError('You have duplicate states in your license list.');
    setLicenses(cleaned);
    if (partnerOnboarding) { void submit({ hasNpi: true }); return; }
    loadNetworks();
    setStep('network-opt');
  };

  // ── Final submit ──────────────────────────────────────────────────
  const submit = async (opts: { hasNpi?: boolean; isVet?: boolean; isPharmacy?: boolean }) => {
    setError('');
    setLoading(true);
    try {
      const payload: any = {
        invitationToken,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        ...(clinicName.trim() && { clinicName: clinicName.trim() })
      };

      if (opts.isVet) {
        // Veterinarian: no NPI, no network. Prescribes under their state vet
        // license(s); credentials are verified manually by an admin (Gate B).
        payload.isVet = true;
        payload.licenses = licenses
          .filter((l) => l.state.trim())
          .map((l) => ({ state: l.state.toUpperCase(), licenseNumber: l.licenseNumber, source: 'self' }));
      } else if (opts.isPharmacy) {
        // Pharmacy: own NPI (required) + state permit(s). No network — the
        // prescribing dr comes in on each transferred script instead.
        payload.isPharmacy = true;
        payload.npiNumber = npiNumber;
        payload.licenses = licenses
          .filter((l) => l.state.trim())
          .map((l) => ({ state: l.state.toUpperCase(), licenseNumber: l.licenseNumber, source: l.source || 'self' }));
      } else if (opts.hasNpi) {
        payload.npiNumber = npiNumber;
        payload.licenses = licenses
          .filter((l) => l.state.trim())
          .map((l) => ({ state: l.state.toUpperCase(), licenseNumber: l.licenseNumber, source: l.source || 'self' }));
        if (useNetwork && selectedNetworkId) {
          payload.useNetworkForUncoveredStates = true;
          payload.telemedNetworkId = selectedNetworkId;
        }
      } else {
        // No NPI → prescribe through the network (telemed-only). Contract signed next.
        if (selectedNetworkId) {
          payload.telemedNetworkId = selectedNetworkId;
        } else {
          payload.telemedNetworkRequested = true;
        }
      }

      const res: any = await apiClient.post('/auth/complete-registration', payload);
      if (!res.success) return setError(res.error || 'Registration failed.');

      // The account is created and authenticated — keep the physician signed in and
      // take them straight to the next step (identity verification / signing the
      // clinic agreement) rather than bouncing them to the login page.
      const loggedIn = await useAuthStore.getState().login(invitation.email, password);
      if (loggedIn) {
        if (partnerOnboarding) sessionStorage.setItem(PARTNER_ONBOARDING_RETURN, partnerOnboarding.id);
        navigate('/id-proofing');
      } else {
        if (partnerOnboarding) { navigate(`/partner-onboarding/${partnerOnboarding.id}`); return; }
        navigate(`/login?message=${encodeURIComponent('Account created! Please sign in to finish setting up your clinic.')}`);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Rendering ─────────────────────────────────────────────────────
  const stepOrder: Step[] = ['personal', 'sms', 'npi-question'];
  const progressIndex = stepOrder.indexOf(step) >= 0 ? stepOrder.indexOf(step) : 2;

  if (step === 'personal') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Welcome — let's set up your account" subtitle={`Signing up as ${invitation.email}${partnerOnboarding ? ` to connect with ${partnerOnboarding.name}` : ''}`}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <input className={inputCls} placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            <div className="relative">
              <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="relative">
            <input className={`${inputCls} bg-gray-50`} value={invitation.email} disabled />
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <div className="relative">
            <input className={inputCls} placeholder="Clinic / practice name (optional)" value={clinicName} onChange={(e) => setClinicName(e.target.value)} />
            <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <div className="relative">
            <input className={inputCls} type="tel" placeholder="Mobile phone (for verification)" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <div className="relative">
            <input className={inputCls} type={showPassword ? 'text' : 'password'} placeholder="Password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="relative">
            <input className={inputCls} type={showPassword ? 'text' : 'password'} placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <button className={btnPrimary} disabled={loading} onClick={handlePersonalNext}>
            {loading ? 'Sending code…' : 'Continue'}
          </button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'sms') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Verify your phone" subtitle={`We texted a 6-digit code to ${phone}`} onBack={() => setStep('personal')}>
        <div className="space-y-4">
          <div className="relative">
            <input
              className="w-full px-3 py-3 text-center text-2xl tracking-[0.5em] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="______"
              inputMode="numeric"
              maxLength={6}
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <button className={btnPrimary} disabled={loading} onClick={handleVerifyOtp}>
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <div className="text-center">
            <button type="button" onClick={handleResend} disabled={resendCooldown > 0}
              className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400">
              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
            </button>
          </div>
        </div>
      </WizardShell>
    );
  }

  if (step === 'npi-question') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Are you going to prescribe under your own NPI?" subtitle="Choose how prescriptions will be written for your orders." onBack={() => setStep('sms')}>
        <div className="space-y-3">
          <button onClick={() => chooseHasNpi(true)}
            className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
            <Stethoscope className="w-6 h-6 text-blue-600 shrink-0" />
            <div>
              <div className="font-medium text-gray-900">Yes, I have an NPI</div>
              <div className="text-sm text-gray-500">We'll verify it and confirm your state licenses.</div>
            </div>
          </button>
          {!partnerOnboarding && <><button onClick={() => chooseHasNpi(false)}
            className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
            <Network className="w-6 h-6 text-blue-600 shrink-0" />
            <div>
              <div className="font-medium text-gray-900">No — I'll use the prescriber network</div>
              <div className="text-sm text-gray-500">Prescribe through our network and sign the clinic agreement.</div>
            </div>
          </button>
          <button onClick={chooseVet}
            className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
            <PawPrint className="w-6 h-6 text-blue-600 shrink-0" />
            <div>
              <div className="font-medium text-gray-900">I'm a veterinarian</div>
              <div className="text-sm text-gray-500">Prescribe under your state veterinary license — no NPI needed.</div>
            </div>
          </button>
          <button onClick={choosePharmacy}
            className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left">
            <Pill className="w-6 h-6 text-blue-600 shrink-0" />
            <div>
              <div className="font-medium text-gray-900">I'm a pharmacy</div>
              <div className="text-sm text-gray-500">Transfer your doctors' prescriptions to us to fill, under your pharmacy NPI.</div>
            </div>
          </button></>}
        </div>
      </WizardShell>
    );
  }

  if (step === 'pharmacy-npi') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Your pharmacy NPI" onBack={() => setStep('npi-question')}
        subtitle="Enter your pharmacy's NPI. It identifies your pharmacy on every order you send us — the doctor who wrote each script is captured on the transfer itself.">
        <div className="space-y-4">
          <div className="relative">
            <input className={inputCls} inputMode="numeric" maxLength={10} placeholder="10-digit pharmacy NPI"
              value={npiNumber} onChange={(e) => setNpiNumber(e.target.value.replace(/\D/g, ''))} />
            <FileText className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <button className={btnPrimary} disabled={loading} onClick={handlePharmacyNpiLookup}>
            {loading ? 'Looking up…' : 'Look up my NPI'}
          </button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'pharmacy-licenses') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Your pharmacy permit(s)" onBack={() => setStep('pharmacy-npi')}
        subtitle="Add the state permit / pharmacy license number for each state you're licensed to operate in.">
        <div className="space-y-3">
          {nppesName && (
            <div className="flex items-center gap-2 text-sm text-gray-700 bg-green-50 rounded-lg p-3">
              <Check className="w-5 h-5 text-green-600 shrink-0" />
              NPI matched <span className="font-medium">{nppesName}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
            <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
            You'll verify your identity in the next step, and an admin will review your credentials before your account can order.
          </div>
          {licenses.map((lic, i) => (
            <div key={i} className="flex items-center gap-2">
              <select className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
                value={lic.state} onChange={(e) => updateLicense(i, 'state', e.target.value)}>
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Pharmacy permit #" value={lic.licenseNumber || ''} onChange={(e) => updateLicense(i, 'licenseNumber', e.target.value)} />
              <button type="button" onClick={() => removeLicense(i)} className="text-gray-400 hover:text-red-500" aria-label="Remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addLicense}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4" /> Add a state
          </button>
          <button className={btnPrimary} disabled={loading} onClick={handlePharmacyLicensesNext}>
            {loading ? 'Creating account…' : 'Create account & continue'}
          </button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'npi-entry') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Enter your NPI" onBack={() => setStep('npi-question')}>
        <div className="space-y-4">
          <div className="relative">
            <input className={inputCls} inputMode="numeric" maxLength={10} placeholder="10-digit NPI"
              value={npiNumber} onChange={(e) => setNpiNumber(e.target.value.replace(/\D/g, ''))} />
            <FileText className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <button className={btnPrimary} disabled={loading} onClick={handleNpiLookup}>
            {loading ? 'Looking up…' : 'Look up my NPI'}
          </button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'licenses') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Confirm your state licenses" onBack={() => setStep('npi-entry')}
        subtitle={nppesName ? `Found: ${nppesName}. It looks like you hold licenses in these states — is this accurate?` : 'We couldn\'t auto-fill from the registry. Add the states you\'re licensed in.'}>
        <div className="space-y-3">
          {licenses.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-2">No licenses yet — add one below.</p>
          )}
          {licenses.map((lic, i) => (
            <div key={i} className="flex items-center gap-2">
              <select className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
                value={lic.state} onChange={(e) => updateLicense(i, 'state', e.target.value)}>
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="License # (optional)" value={lic.licenseNumber || ''} onChange={(e) => updateLicense(i, 'licenseNumber', e.target.value)} />
              {lic.source === 'nppes' && <span title="From registry"><Check className="w-4 h-4 text-green-500 shrink-0" /></span>}
              <button type="button" onClick={() => removeLicense(i)} className="text-gray-400 hover:text-red-500" aria-label="Remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addLicense}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4" /> Add a state
          </button>
          <button className={btnPrimary} disabled={loading} onClick={handleLicensesNext}>{loading ? 'Creating account…' : 'Continue'}</button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'network-opt') {
    const licensedStates = licenses.filter((l) => l.state).map((l) => l.state.toUpperCase());
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Cover all 50 states?" onBack={() => setStep('licenses')}
        subtitle={`You're licensed in ${licensedStates.length} state${licensedStates.length === 1 ? '' : 's'}. Use our prescriber network to cover the rest?`}>
        <div className="space-y-4">
          <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors"
            style={{ borderColor: useNetwork ? '#2563eb' : '#e5e7eb', background: useNetwork ? '#eff6ff' : 'white' }}>
            <input type="checkbox" checked={useNetwork} onChange={(e) => setUseNetwork(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-sm text-gray-700">
              <span className="font-medium text-gray-900">Yes, use the network for states I'm not licensed in</span>
              <span className="block text-gray-500 mt-0.5">Orders shipping to states you don't hold a license in are prescribed by a network provider (per-visit fee applies). You'll sign the clinic agreement.</span>
            </span>
          </label>

          {useNetwork && (
            <div>
              {networks.length === 0 && <p className="text-sm text-gray-500">No networks are available right now — you can still finish and an admin will connect one.</p>}
              {networks.length === 1 && (
                <div className="text-sm text-gray-700 p-3 bg-gray-50 rounded-lg">
                  Network: <span className="font-medium">{networks[0].name}</span> (${(networks[0].perVisitFeeCents / 100).toFixed(2)}/visit)
                </div>
              )}
              {networks.length > 1 && (
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedNetworkId} onChange={(e) => setSelectedNetworkId(e.target.value)}>
                  <option value="">Select a network…</option>
                  {networks.map((n) => <option key={n._id} value={n._id}>{n.name} (${(n.perVisitFeeCents / 100).toFixed(2)}/visit)</option>)}
                </select>
              )}
            </div>
          )}

          <button className={btnPrimary} disabled={loading || (useNetwork && networks.length > 1 && !selectedNetworkId)} onClick={() => submit({ hasNpi: true })}>
            {loading ? 'Creating account…' : 'Finish'}
          </button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'no-npi-network') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Prescribe through our network" onBack={() => setStep('npi-question')}
        subtitle="Your orders are prescribed by a licensed network provider based on the patient's state. You'll sign the clinic agreement after creating your account.">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
            <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
            No NPI needed — the network covers prescribing in all covered states.
          </div>
          {networks.length === 0 && <p className="text-sm text-gray-500">No networks are available right now — you can still finish and an admin will connect one before you can order.</p>}
          {networks.length === 1 && (
            <div className="text-sm text-gray-700 p-3 bg-gray-50 rounded-lg">
              Network: <span className="font-medium">{networks[0].name}</span> (${(networks[0].perVisitFeeCents / 100).toFixed(2)}/visit)
            </div>
          )}
          {networks.length > 1 && (
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedNetworkId} onChange={(e) => setSelectedNetworkId(e.target.value)}>
              <option value="">Select a network…</option>
              {networks.map((n) => <option key={n._id} value={n._id}>{n.name} (${(n.perVisitFeeCents / 100).toFixed(2)}/visit)</option>)}
            </select>
          )}
          <button className={btnPrimary} disabled={loading || (networks.length > 1 && !selectedNetworkId)} onClick={() => submit({ hasNpi: false })}>
            {loading ? 'Creating account…' : 'Create account & continue to agreement'}
          </button>
        </div>
      </WizardShell>
    );
  }

  if (step === 'vet-licenses') {
    return (
      <WizardShell error={error} progressIndex={progressIndex} title="Your veterinary license(s)" onBack={() => setStep('npi-question')}
        subtitle="Add each state you're licensed to practice veterinary medicine in. Your license number is sent to the pharmacy in place of an NPI, so it's required.">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
            <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
            An admin will verify your license before your account can order.
          </div>
          {licenses.map((lic, i) => (
            <div key={i} className="flex items-center gap-2">
              <select className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
                value={lic.state} onChange={(e) => updateLicense(i, 'state', e.target.value)}>
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Vet license #" value={lic.licenseNumber || ''} onChange={(e) => updateLicense(i, 'licenseNumber', e.target.value)} />
              <button type="button" onClick={() => removeLicense(i)} className="text-gray-400 hover:text-red-500" aria-label="Remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addLicense}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4" /> Add a state
          </button>
          <button className={btnPrimary} disabled={loading} onClick={handleVetLicensesNext}>
            {loading ? 'Creating account…' : 'Create account & continue to agreement'}
          </button>
        </div>
      </WizardShell>
    );
  }

  return null;
};
